"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { HeroBody } from "./HeroBody";
import { useReducedMotion } from "./useReducedMotion";
import type { HeroContent } from "./types";

export function GlassHero({ content }: { content: HeroContent }) {
  const rootRef = useRef<HTMLElement | null>(null);
  const paneA = useRef<HTMLDivElement | null>(null);
  const paneB = useRef<HTMLDivElement | null>(null);
  const paneC = useRef<HTMLDivElement | null>(null);
  const sweepRef = useRef<HTMLDivElement | null>(null);
  const badgeRef = useRef<HTMLSpanElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const subtitleRef = useRef<HTMLParagraphElement | null>(null);
  const ctasRef = useRef<HTMLDivElement | null>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (!rootRef.current) return;
    const ctx = gsap.context(() => {
      const intro = [badgeRef.current, titleRef.current, subtitleRef.current, ctasRef.current].filter(Boolean);
      if (reduced) {
        gsap.set(intro, { opacity: 1, y: 0 });
        return;
      }
      gsap.set(intro, { opacity: 0, y: 24 });
      gsap.to(intro, {
        opacity: 1,
        y: 0,
        duration: 0.88,
        ease: "power3.out",
        stagger: 0.1,
        delay: 0.12,
      });

      [paneA, paneB, paneC].forEach((r, i) => {
        if (!r.current) return;
        gsap.to(r.current, {
          y: i === 1 ? -14 : 10,
          rotation: i === 0 ? -2 : i === 2 ? 3 : 0,
          duration: 5 + i,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
        });
      });

      if (sweepRef.current) {
        gsap.fromTo(
          sweepRef.current,
          { xPercent: -120, opacity: 0 },
          {
            xPercent: 220,
            opacity: 0.55,
            duration: 4.2,
            ease: "power2.inOut",
            repeat: -1,
            repeatDelay: 2.8,
          }
        );
      }
    }, rootRef);
    return () => ctx.revert();
  }, [reduced]);

  const paneClass =
    "absolute rounded-[2rem] border border-white/[0.14] bg-white/[0.06] shadow-[0_24px_80px_rgba(0,0,0,0.35)] backdrop-blur-2xl";

  return (
    <section
      ref={rootRef}
      className="hero-section hero-grain relative isolate overflow-hidden bg-[var(--primary-darker)] px-4 py-20 md:py-28"
      aria-label="Hero"
    >
      <div aria-hidden className="absolute inset-0">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 90% 70% at 50% -10%, color-mix(in oklch, var(--accent) 35%, transparent) 0%, transparent 55%), radial-gradient(circle at 85% 75%, color-mix(in oklch, var(--primary-light) 40%, transparent) 0%, transparent 42%)",
          }}
        />
        <div
          ref={paneA}
          className={paneClass}
          style={{ width: "42%", height: "38%", left: "-8%", top: "18%", transform: "rotate(-8deg)" }}
        />
        <div
          ref={paneB}
          className={paneClass}
          style={{ width: "36%", height: "32%", right: "-6%", top: "8%", transform: "rotate(6deg)" }}
        />
        <div
          ref={paneC}
          className={paneClass}
          style={{ width: "48%", height: "28%", left: "22%", bottom: "6%", transform: "rotate(-3deg)" }}
        />
        <div
          ref={sweepRef}
          className="pointer-events-none absolute inset-y-0 w-[38%] opacity-0"
          style={{
            background:
              "linear-gradient(105deg, transparent 0%, rgba(255,255,255,0.14) 45%, transparent 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, transparent 45%, rgba(0,0,0,0.45) 100%), radial-gradient(ellipse 100% 80% at 50% 100%, rgba(0,0,0,0.35) 0%, transparent 55%)",
          }}
        />
      </div>

      <HeroBody
        content={content}
        primaryVariant="glow"
        secondaryVariant="outline"
        refs={{ badge: badgeRef, title: titleRef, subtitle: subtitleRef, ctas: ctasRef }}
      />
    </section>
  );
}
